
import moment from 'moment';
import type { Lesson } from '../types/lessons';
import type { OutTypes } from './schedules';

const firstDay = moment.utc('1970-01-05');

function fromTimeString (
  day:        number,
  timeString: string
): moment.Moment {
  const [hours, minutes, seconds] = timeString.split(':').map((time: string) => parseInt(time));
  return firstDay.clone().add(day, 'day')
    .set('hour', hours)
    .set('minute', minutes);
}

export default function (
  lessons:          Lesson[] | undefined,
  courses:          OutTypes.course[] | undefined,
  teachers:         OutTypes.teacher[] | undefined,
  importErrors:     string[],
  importWarnings:   string[],
): OutTypes.event[] {

  // create schedule_group (group) to course map for quick lookup
  const group2course = new Map<string, OutTypes.course>();
  courses?.forEach(c => group2course.set(c.ids!, c));

  // create teacher set for quick lookup
  const teacherSet = new Set<string>();
  teachers?.forEach(t => teacherSet.add(t.ids!));

  const events: OutTypes.event[] = [];
  lessons?.forEach(l => {

    // check number of groups (schedule_groups = course) to ensure that only one exists
    if (l.info.groups?.length != 1) {
      const warn = `The lesson with id "${l.id} contained ${l.info.groups?.length} number of groups (schedule_groups = courses) and was therefore omitted: a lesson shall contain only a single group.`;
      importWarnings.push(warn);
      return;
    }
    const course_id = l.info.groups[0].id.toString();

    // ensure the course exists
    const course = group2course.get(course_id);
    if (!course) {
      const warn = `The lesson with id "${l.id}" referenced the unknown/faulty schedule_group (course) "${course_id}" and was therefore ignored.`;
      importWarnings.push(warn);
      return;
    }

    const start             = fromTimeString(l.weekday, l.info.start);
    const end               = fromTimeString(l.weekday, l.info.end);
    const preferredDuration = l.info.duration;
    const duration          = l.info.duration;

    // store only teachers if they mismatch teachers of parent course
    let eventTeachers: string[] | undefined;
    if (!course.teachers || !course.teachers?.every(x => l.info.teachers?.some(y => y.id.toString() == x.to))) {
      eventTeachers = l.info.teachers?.map(x => x.id.toString());

      // ensure that the teachers exist
      for (const teacher of eventTeachers ?? []) {
        if (!teacherSet.has(teacher)) {
          const warn = `The lesson with id "${l.id}" referenced the unknown teacher "${teacher}" and was therefore ignored.`;
          importWarnings.push(warn);
          return;
        }
      }
    }

    // location
    const location = l.info.room?.id.toString();

    events.push({
      ids:    l.id.toString(),
      start,
      end,
      preferredDuration,
      duration,
      course: course_id,
      ...location && { inLocations: [ location ], locations: [{ locations: [ location ] }] },
      ...(eventTeachers && { teachers: eventTeachers.map((to) => ({ to })) })
    } satisfies OutTypes.event);
  });

  return events;
}
